import { Divider } from '@mui/material';
import * as React from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { IconSvg } from 'src/assets';
import { LocationProps } from 'src/common/api/response';
import { useLocationMutation } from 'src/common/hooks';
import { Container, IconButton, Row } from 'src/components';
import { NewButtonInput } from 'src/components/bases/NewButtonInput';
import { ProvinceModal } from 'src/components/layout/provinceModal/ProvinceModal';
import { AppColors, CustomShadow } from 'src/themes/Colors';
import { metrics, pixelScaler } from 'src/themes/Metrics';
import styled from 'styled-components/native';

const Styled = {
  SwapButton: styled(IconButton)`
    background-color: white;
    border-width: ${StyleSheet.hairlineWidth}px;
    border-radius: 10px;
    height: ${pixelScaler(32)}px;
    width: ${pixelScaler(32)}px;
    border-color: ${AppColors.border};
  `,
};

export const OriginDestinationCard = ({
  control,
  onFocus,
  onBlur,
}: {
  control: any;
  onFocus: () => void;
  onBlur: () => void;
}) => {
  const { t } = useTranslation();
  const fromLocation = useWatch({ control, name: 'fromLocation' });
  const toLocation = useWatch({ control, name: 'toLocation' });
  const fromLocationController = useController({ control, name: 'fromLocation' });
  const toLocationController = useController({ control, name: 'toLocation' });
  const [provinceModal, setProvinceModal] = React.useState<string>('');
  const [fromLocationData, setFromLocationData] = React.useState<LocationProps[]>([]);
  const [toLocationData, setToLocationData] = React.useState<LocationProps[]>([]);
  const { mutateAsync, isLoading, error } = useLocationMutation();

  const handleFetchData = async () => {
    let response = null;
    if (provinceModal === 'fromLocation') {
      response = await mutateAsync({ toLocationId: toLocation?.id }).then(res => res.data.data);
      setFromLocationData(response);
    }

    if (provinceModal === 'toLocation') {
      response = await mutateAsync({ fromLocationId: fromLocation?.id }).then(res => res.data.data);
      setToLocationData(response);
    }
  };

  React.useEffect(() => {
    handleFetchData();
  }, [provinceModal, toLocation, fromLocation]);

  const handleSwapPressed = () => {
    const from = { ...toLocation };
    const to = { ...fromLocation };
    fromLocationController.field.onChange(from);
    toLocationController.field.onChange(to);
  };

  const handleOpenWhereTo = () => setProvinceModal('toLocation');

  const handleOpenWhereFrom = () => setProvinceModal('fromLocation');

  const handleCloseProvinceModal = () => setProvinceModal('');

  return (
    <Row
      width="100%"
      borderRadius={metrics.borderRadius}
      backgroundColor={AppColors.white}
      paddingHorizontal={metrics.large}
      paddingTop={metrics.large}
      paddingRight={0}
      style={[CustomShadow, { zIndex: 999 }]}>
      <Container align="center" paddingTop={pixelScaler(2)}>
        <IconSvg.LoginOrigin />
        <Container height={metrics.inputHeight - pixelScaler(4)} width={2} backgroundColor={AppColors.border} />
        <IconSvg.LoginDestination />
      </Container>
      <Container flex>
        <NewButtonInput
          row
          labelLang="whereFrom"
          placeholderLang="whereFrom"
          onPress={handleOpenWhereFrom}
          value={fromLocationController.field.value?.name}
          onChange={fromLocationController.field.onChange}
          clearValue={null}
        />

        <Container>
          <Divider style={{ marginBottom: metrics.small }} />
          <Container
            style={{
              position: 'absolute',
              top: -pixelScaler(16),
              right: pixelScaler(20),
              overflow: 'hidden',
            }}>
            <Styled.SwapButton
              disabled={!fromLocation || !toLocation}
              icon={<IconSvg.Swaper />}
              onPress={handleSwapPressed}
            />
          </Container>
        </Container>
        <Container paddingTop={metrics.small} />
        <NewButtonInput
          row
          labelLang="whereTo"
          placeholderLang="whereTo"
          onPress={handleOpenWhereTo}
          value={toLocationController.field.value?.name}
          onChange={toLocationController.field.onChange}
          clearValue={null}
        />
      </Container>

      <ProvinceModal
        label={provinceModal === 'fromLocation' ? 'chooseOrigin' : 'chooseDestination'}
        onClose={handleCloseProvinceModal}
        isVisible={provinceModal !== ''}
        isLoading={isLoading}
        onProvinceSelected={(provinceSelected: LocationProps) => {
          console.log({ provinceSelected });
          if (provinceModal === 'fromLocation') {
            fromLocationController.field.onChange(provinceSelected);
          } else {
            toLocationController.field.onChange(provinceSelected);
          }
          handleCloseProvinceModal();
        }}
        provinces={provinceModal === 'fromLocation' ? fromLocationData : toLocationData}
        error={error}
      />
    </Row>
  );
};

// const PickProvinceButton = ({
//   control,
//   name,
//   placeholder,
//   label,
//   disabledItems,
//   disabled,
//   fromLocationId,
// }: {
//   control: any;
//   name: any;
//   placeholder: string;
//   label: string;
//   disabledItems: string[];
//   disabled?: boolean;
//   fromLocationId?: string;
// }) => {
//   const [provinceVisible, setProvinceVisible] = React.useState<boolean>(false);

//   const handlePress = () => setProvinceVisible(true);

//   const handleCloseProvinceModal = () => setProvinceVisible(false);

//   return (
//     <Controller
//       control={control}
//       name={name}
//       render={({ field: { value, onChange }, fieldState: { error } }) => {
//         return (
//           <>
//             <TouchableOpacity
//               disabled={disabled}
//               activeOpacity={0.8}
//               style={{ height: metrics.inputHeight, width: '100%', justifyContent: 'center' }}
//               onPress={handlePress}>
//               <RegularText fontSize={value?.name ? 'medium' : 'large'} color={AppColors.gray}>
//                 {value?.name ? label : placeholder}
//               </RegularText>
//               <RegularText>{value?.name}</RegularText>
//             </TouchableOpacity>
//             {error && (
//               <RegularText textAlign="left" color={AppColors.red} fontSize="small">
//                 {error.message}
//               </RegularText>
//             )}
//             {provinceVisible && (
//               <ProvinceModal
//                 fromLocationId={fromLocationId}
//                 label={name === 'fromLocation' ? 'chooseOrigin' : 'chooseDestination'}
//                 onClose={handleCloseProvinceModal}
//                 disabledItems={disabledItems}
//                 isVisible={provinceVisible}
//                 onProvinceSelected={(provinceSelected: LocationProps) => {
//                   onChange(provinceSelected);
//                   handleCloseProvinceModal();
//                 }}
//               />
//             )}
//           </>
//         );
//       }}
//     />
//   );
// };
